import { Link, useNavigate } from "react-router";
import { DashboardCard } from "../molecules/DashboardCard";
import { DueDateCell } from "../molecules/DueDateCell";
import { type WorkflowListItem } from "~/api/types";
import { dateDiff } from "~/helpers/date";
import { useTranslation } from "react-i18next";
import NoResult from "../molecules/NoResult";
import SimpleDataTable, {
  type TableColumn,
} from "../molecules/SimpleDataTable";
import { CenterSpinner } from "../atoms/spinner";
import { Button } from "~/atoms/button";
import { useWorkflows } from "~/api/v2/useWorkflows";
import { DocumentVersionStatus } from "~/config/types";

const UPCOMING_REVIEW_DAYS_THRESHOLD = 90;

const UpcomingWorkflowsContainer = () => {
  const { t } = useTranslation();

  const columns: TableColumn<WorkflowListItem>[] = [
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) =>
        a.documentVersion.title.localeCompare(b.documentVersion.title),
      cell: (wf) => <span>{wf.documentVersion.title}</span>,
    },
    {
      header: t(($) => $.commons.document.startScheduledReview),
      defaultSort: true,
      compareFn: (a, b) =>
        dateDiff(a.document.scheduleReviewFrom, b.document.scheduleReviewFrom),
      cell: (v) => <DueDateCell dueDate={v.document.scheduleReviewFrom} />,
      tableHeadClassName: "w-32",
    },
    {
      header: t(($) => $.commons.document.startScheduledDueDate),
      compareFn: (a, b) =>
        dateDiff(
          a.documentVersion.reviewTo ?? a.document.scheduleReviewTo,
          b.documentVersion.reviewTo ?? b.document.scheduleReviewTo,
        ),
      cell: (wf) => (
        <DueDateCell
          dueDate={wf.documentVersion.reviewTo ?? wf.document.scheduleReviewTo}
        />
      ),
      tableHeadClassName: "w-32",
    },
  ];
  const navigate = useNavigate();
  const { data: workflows, isLoading } = useWorkflows();

  const upcomingWorkflows = workflows?.filter(
    (wf) =>
      !wf.isOngoing &&
      wf.documentVersion.status === DocumentVersionStatus.draft &&
      wf.document.scheduleReviewFrom &&
      dateDiff(wf.document.scheduleReviewFrom, new Date(), "day") <=
        UPCOMING_REVIEW_DAYS_THRESHOLD,
  );

  const title = t(($) => $.dashboard.upcoming.workflows);

  if (isLoading) {
    return (
      <DashboardCard title={title} className="h-80">
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (upcomingWorkflows?.length === 0) {
    return (
      <DashboardCard title={title}>
        <NoResult className="h-full" />
      </DashboardCard>
    );
  }

  const button = (
    <Button variant="link" asChild>
      <Link to="workflows?tab=upcoming">
        {t(($) => $.commons.buttons.seeAll)}
      </Link>
    </Button>
  );

  return (
    <DashboardCard title={title} button={button}>
      <SimpleDataTable
        data={upcomingWorkflows ?? []}
        columns={columns}
        onClickRow={(i) => navigate(`/documents/${i.document.id}?tab=workflow`)}
      />
    </DashboardCard>
  );
};

export default UpcomingWorkflowsContainer;
